import React, { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { GharDialog } from "./GharDialog";
import { GHARS, GHAR_TYPE_HI, WEEKDAYS_HI, saveGhars } from "../data/rssbData";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Phone, UserRound } from "lucide-react";

const Stat = ({ label, v }) => (
  <div className="text-center">
    <div className="text-[10px] text-[#9a7a5a]">{label}</div>
    <div className="font-bold text-[#3a1212]">{v ?? "—"}</div>
  </div>
);

const GharCard = ({ g, sr, onEdit, onDelete }) => (
  <div data-testid={`ghar-card-${g.id}`} className="animate-fade-up bg-[#fffdf7] border border-[#e3d6b6] rounded-lg p-3 hover:border-[#6e1b1b]/50 transition-colors">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="font-serif-dev font-semibold text-[#3a1212]">
          {sr}. {g.hi}<span className="text-[#6e1b1b]">–{GHAR_TYPE_HI[g.type]}</span>
        </div>
        <div className="text-[11px] text-[#9a7a5a] font-dev">समूह {g.group} · {g.days.map((d) => WEEKDAYS_HI[d]).join(", ")}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Badge className="bg-[#e3d6b6] text-[#5a2a24] text-[10px]">{g.type}</Badge>
        {g.mw && g.mw !== "NO" && <Badge className="bg-[#6e1b1b] text-[#f3ead6] text-[10px]">{g.mw}</Badge>}
        {g.noLcd && <Badge data-testid={`ghar-nolcd-badge-${g.id}`} className="bg-[#f3ead6] text-[#6e1b1b] border border-[#6e1b1b]/40 text-[10px]">LCD नहीं</Badge>}
        <button data-testid={`ghar-card-edit-${g.id}`} onClick={onEdit} className="p-1.5 rounded text-[#6e1b1b] hover:bg-[#f3ead6]" aria-label="edit"><Pencil className="w-3.5 h-3.5" /></button>
        <button data-testid={`ghar-card-delete-${g.id}`} onClick={onDelete} className="p-1.5 rounded text-[#b91c1c] hover:bg-[#f3ead6]" aria-label="delete"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
    <div className="mt-2 text-xs text-[#5a2a24] font-dev flex flex-wrap gap-x-3 gap-y-0.5">
      <span className="flex items-center gap-1"><UserRound className="w-3 h-3" /> {g.secretaryHi || "—"}</span>
      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {g.secretaryMobile || "—"}</span>
    </div>
    <div className="mt-2 grid grid-cols-5 gap-1 bg-[#f6eeda] rounded-md py-1.5 text-xs">
      <Stat label="पाठी ड्यूटी" v={g.pathiDuty} />
      <Stat label="SK" v={g.nSK} />
      <Stat label="SR" v={g.nSR} />
      <Stat label="BSK" v={g.nBSK} />
      <Stat label="पाठी" v={g.nPathi} />
    </div>
  </div>
);

export const GharDirectory = ({ q = "", onChange }) => {
  const [, setVer] = useState(0);
  const [dlg, setDlg] = useState({ open: false, ghar: null });
  const commit = (list, msg) => {
    saveGhars(list);
    setVer((v) => v + 1);
    onChange?.();
    toast.success(msg);
  };
  const onSave = (g) => {
    const exists = GHARS.some((x) => x.id === g.id);
    commit(exists ? GHARS.map((x) => (x.id === g.id ? g : x)) : [...GHARS, g], exists ? "सत्संग घर अपडेट हुआ" : "सत्संग घर जोड़ा गया");
    setDlg({ open: false, ghar: null });
  };
  const onDelete = (g) => window.confirm(`${g.hi} को सूची से हटाएं?`) && commit(GHARS.filter((x) => x.id !== g.id), "सत्संग घर हटाया गया");
  const list = GHARS.map((g, i) => ({ g, sr: i + 1 })).filter(
    ({ g }) => !q || g.hi.includes(q) || (g.en || "").toLowerCase().includes(q.toLowerCase()) || (g.secretaryHi || "").includes(q)
  );
  return (
    <div className="space-y-3" data-testid="ghar-directory">
      <div className="flex justify-end">
        <Button data-testid="ghar-dir-add" onClick={() => setDlg({ open: true, ghar: null })} className="bg-gradient-to-b from-[#7a1f1f] to-[#5e1414] border border-[#431010] text-[#f3ead6] font-semibold gap-1 font-dev">
          <Plus className="w-4 h-4" /> नया सत्संग घर जोड़ें
        </Button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {list.map(({ g, sr }) => (
          <GharCard key={g.id} g={g} sr={sr} onEdit={() => setDlg({ open: true, ghar: g })} onDelete={() => onDelete(g)} />
        ))}
        {!list.length && <div className="col-span-full text-center text-[#9a7a5a] py-10">कोई परिणाम नहीं मिला</div>}
      </div>
      <GharDialog open={dlg.open} ghar={dlg.ghar} onClose={() => setDlg({ open: false, ghar: null })} onSave={onSave} />
    </div>
  );
};
